import React, { ForwardedRef, useImperativeHandle, useRef, useEffect, useState } from 'react';
import * as echarts from 'echarts';
import type {GeoJSON} from "echarts";
import Chart from '..';
import { Icon } from "tea-component";
import { ChartRef } from '../_config/_interface';
import {
  COLORS_MAP
} from "@dbl/components/CustomComponents/Chart/_config/colors";
import {
  calculateMapZoom, getMapCenter,
  MapChartProps,
  NameMap,
  setMapColor, setMapSeriesOption, TMapType,
  useMapOptions
} from "@customComp/Chart/chartConfigs/mapChartHooks";
import { importJSON } from "@intelligence/utils/echartsUtils";
import Loading from "@/pages/dashboard/components/Loadings";
import { getMapLabels } from '@dbl/services/chart';
import { setDataLabel } from '../chartConfigs/plugs';
import { getLocale } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import {formatDisplayValue} from "@customComp/Chart/_config/_utils";

let mapLoading = false;
// 地图文件缓存
const mapCache: {[key in TMapType]?: GeoJSON} = {};
// 中英文映射名称缓存
const mapNamesCache: {[key in TMapType]?: NameMap} = {};

const MapChartInner: React.ForwardRefRenderFunction<ChartRef, MapChartProps> = (
  props,
  ref: ForwardedRef<ChartRef>
) => {
  const { mapType = 'world', metricInfo } = props;
  const isEn = getLocale() === LANGUAGE_EN;
  const chartRef = useRef<ChartRef>(null);
  // 在报表编辑页面时, 很容易触发render, 导致放大缩小后的地图 恢复原来的zoom, 所以这里采用设置zoom状态, 改变option的方式, 而不是直接在方法内部setOption()触发地图渲染
  const [zoom, setZoom] = useState(1.2);
  const [center, setCenter] = useState<undefined| [number, number]>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  useImperativeHandle(ref, () => ({
    instance: chartRef?.current?.instance,
  }));
  
  useEffect(() => {
    const getMapData = async () => {
      try {
        // 在报表中依然有可能出现两次请求
        // React 的并发渲染问题
        // 当多个 MapChart 组件同时挂载时，它们的 useEffect 可能几乎同时执行。
        // 由于 mapLoading 初始值是 false，两个组件都可能进入 if (!mapLoading) 条件，导致重复请求。
        // mapLoading 的赋值是异步的​​
        // 即使第一个组件进入 if (!mapLoading) 并设置 mapLoading = true，第二个组件可能已经在检查 mapLoading 时发现它仍然是 false（因为 JavaScript 是单线程的，但 React 的渲染可能是并发的）。
        
        if (!mapCache[mapType] && !mapNamesCache[mapType] && !mapLoading) {
          setLoading(true);
          mapLoading = true;
          const mapPromise = importJSON(
            'https://ogdb-cdn.intlgame.cn/front/static/dblWorld.json',
          );
          const namesPromise = getMapLabels({map_type: mapType});
          
          const [mapJSON, namesMap] = await Promise.all([mapPromise, namesPromise]);
          mapCache[mapType] = mapJSON as GeoJSON;
          mapNamesCache[mapType] = namesMap as NameMap;
          mapLoading = false;
        }
        
        // 注册地图
        if (!echarts.getMap(`dbl_${mapType}`)) {
          echarts.registerMap(`dbl_${mapType}`, mapCache[mapType]);
          setLoading(false); // 为了触发地图组件渲染
        }
        
      } catch (error) {
        console.error('地图JSON文件或中英文名称数据加载失败:', error);
        mapLoading = false;
        setLoading(false);
      }
    };
    getMapData();
  }, [mapType]);
  
  const { mapColors } = props.chartConfig;
  const colors = mapColors?.colors?.map(color => color?.hexa) || COLORS_MAP.blue.colors;
  let option = useMapOptions(props, mapNamesCache[mapType]);
  option = setMapSeriesOption(option, {zoom, center});
  option = setMapColor({option, colors});
  option = setDataLabel(option, props.chartConfig, {
    labelAuto: false,
    isNewline: false,
    filterZero: true,
    dimensionFormatter: (params) => {
      const { name: code, } = params?.data || { name: params?.name };
      const { name_en = '-', name_zh = '-', name } = mapNamesCache[mapType]?.[code] || {};
      const dimensionName = (isEn ? name_en : name_zh) || name || code || '-';
      return dimensionName;
    },
    metricFormatter: (param, isPercent) => {
      // 地图只有一个指标
      const {accuracy, unit} = metricInfo || {accuracy: 0, unit: 'bmk'};
      return formatDisplayValue({
        value: param.value,
        isPercent,
        accuracy,
        unit,
      });
    },
  });
  // 注册地图成功后再渲染
  return !loading && echarts.getMap(`dbl_${mapType}`) ? (
    <div className="analysis-chart-container dbl-map-chart">
      <div className="analysis-chart-content">
        <Chart ref={chartRef} option={option} notMerge={true}/>
      </div>
      <div className="dbl-map-chart-zoom-btn">
        <Icon
          className="dbl-map-chart-zoom-btn-plus"
          type="plus"
          onClick={(event) => {
            event.stopPropagation();
            setZoom(calculateMapZoom(zoom, 'zoomIn'));
            setCenter(getMapCenter(chartRef?.current?.instance()));
          }}
        />
        <Icon
          className="dbl-map-chart-zoom-btn-minus"
          type="minus"
          onClick={(event) => {
            event.stopPropagation();
            setZoom(calculateMapZoom(zoom, 'zoomOut'));
            setCenter(getMapCenter(chartRef?.current?.instance()));
          }}
        />
      </div>
    </div>
  ) : (<Loading/>);
};

const MapChart = React.forwardRef(MapChartInner);

export default MapChart;